import { PanelContent } from "../../lib/panel/panel-content";
import { useTranslation } from "react-i18next";
import { FormField } from "@babackapp/ui/components/form";
import { useWorkflowForm } from "../../context/workflows-editor-workflows-context";
import { useNodeFormPath } from "../../hooks/use-node-form-path";
import {
  AVAILABLE_LOCALES,
  TranslatableField,
} from "@babackapp/ui/components/translatable-field";
import { BlockEditorInput } from "@babackapp/blocks-editor/block-editor-input";
import { useVariablesDictionary } from "@babackapp/variables-registry";
import { useWorkflowsEditorConfig } from "../../context/workflows-editor-config-context";
import useCurrentShopSuspense from "../../hooks/use-current-shop-suspense";
import { useEffect, useMemo } from "react";
import {
  getDomainDefaultLocale,
  getDomainLocales,
} from "../../lib/domain-locales";
import { getDefaultTemplatesForLocale } from "./default-templates";

export function PanelGorgiasTicket() {
  const { t } = useTranslation("trackoffice");
  const form = useWorkflowForm();
  const { data: shop } = useCurrentShopSuspense();
  const { variables } = useVariablesDictionary();
  // The default texts reference domain variables ({{parcel.*}} vs {{return.*}}).
  const { domain } = useWorkflowsEditorConfig();

  const titlePath = useNodeFormPath("config.title");
  const descriptionPath = useNodeFormPath("config.description");

  const availableLocales = useMemo(() => {
    return getDomainLocales(shop, domain).map((locale) => ({
      value: locale,
      label:
        AVAILABLE_LOCALES[locale as keyof typeof AVAILABLE_LOCALES]?.label ||
        locale,
      flag:
        AVAILABLE_LOCALES[locale as keyof typeof AVAILABLE_LOCALES]?.flag || "",
    }));
  }, [shop, domain]);

  const defaultLocale = getDomainDefaultLocale(shop, domain);

  useEffect(() => {
    if (!titlePath || !descriptionPath) return;

    const title = form.getValues(titlePath);
    const description = form.getValues(descriptionPath);

    const needsInitialization =
      !title ||
      Object.keys(title).length === 0 ||
      !description ||
      Object.keys(description).length === 0;

    if (needsInitialization) {
      const defaultTitle: Record<string, string> = {};
      const defaultDescription: Record<string, string> = {};

      availableLocales.forEach((locale) => {
        if (!title || !(title as Record<string, string>)[locale.value]) {
          const templates = getDefaultTemplatesForLocale(locale.value, domain);
          defaultTitle[locale.value] = templates.title;
        }
        if (
          !description ||
          !(description as Record<string, string>)[locale.value]
        ) {
          const templates = getDefaultTemplatesForLocale(locale.value, domain);
          defaultDescription[locale.value] = templates.description;
        }
      });

      if (!title || Object.keys(title).length === 0) {
        form.setValue(titlePath, defaultTitle, {
          shouldDirty: false,
        });
      }
      if (!description || Object.keys(description).length === 0) {
        form.setValue(descriptionPath, defaultDescription, {
          shouldDirty: false,
        });
      }
    }
  }, [titlePath, descriptionPath, availableLocales, form, domain]);

  if (!titlePath || !descriptionPath) {
    return null;
  }

  return (
    <PanelContent
      title={t("workflowsEditor.gorgiasTicket.panelTitle", "Gorgias Ticket")}
    >
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground">
          {t(
            "workflowsEditor.gorgiasTicket.description",
            "Customize the title and description of the Gorgias support ticket. You can use variables to insert dynamic data.",
          )}
        </p>

        <FormField
          control={form.control}
          name={titlePath}
          render={({ field }) => (
            <div className="flex flex-col gap-1">
              <span className="font-medium inline-flex gap-1 items-center text-muted-foreground text-xs">
                {t("workflowsEditor.gorgiasTicket.title", "Ticket Title")}
              </span>
              <TranslatableField
                value={field.value as Record<string, string>}
                onChange={field.onChange}
                locales={availableLocales.map((l) => l.value)}
                defaultLocale={defaultLocale}
                placeholder={t(
                  "workflowsEditor.gorgiasTicket.titlePlaceholder",
                  "Enter ticket title...",
                )}
                className="text-sm"
                renderInput={({ value, onChange, placeholder }) => (
                  <BlockEditorInput
                    variables={variables}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    outputFormat="text"
                  />
                )}
              />
            </div>
          )}
        />

        <FormField
          control={form.control}
          name={descriptionPath}
          render={({ field }) => (
            <div className="flex flex-col gap-1">
              <span className="font-medium inline-flex gap-1 items-center text-muted-foreground text-xs">
                {t(
                  "workflowsEditor.gorgiasTicket.description",
                  "Ticket Description",
                )}
              </span>
              <TranslatableField
                value={field.value as Record<string, string>}
                onChange={field.onChange}
                locales={availableLocales.map((l) => l.value)}
                defaultLocale={defaultLocale}
                placeholder={t(
                  "workflowsEditor.gorgiasTicket.descriptionPlaceholder",
                  "Enter ticket description...",
                )}
                className="text-sm"
                renderInput={({ value, onChange, placeholder }) => (
                  <BlockEditorInput
                    variables={variables}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    outputFormat="text"
                    className="min-h-[100px]"
                  />
                )}
              />
            </div>
          )}
        />
      </div>
    </PanelContent>
  );
}
